import { createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { PricingNotFor, PricingTierGrid } from "@/components/pricing/PricingTierGrid";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FaqAccordion } from "@/components/FaqAccordion";
import { ReviewedBy } from "@/components/ReviewedBy";
import { RelatedReading } from "@/components/RelatedReading";
import { AiAnswersCallout } from "@/components/AiAnswersCallout";
import { PRICING_TIERS, NOT_FOR, PRICING_FAQS } from "@/content/pricing";
import { SITE } from "@/content/site";
import { buildSeo } from "@/lib/seo";
import { NextSteps } from "@/components/NextSteps";

const TITLE = "SEO, AEO & GEO Pricing & Packages (2026) | Freelancer Tamal";
const DESC =
  "Transparent SEO, AEO and GEO pricing for Bangladesh and international clients: audits from ৳20,000 / $2,500, local retainers from ৳15,000 / $1,500, AEO sprints from ৳25,000 / $3,000, GEO retainers from ৳30,000 / $3,000.";

export const Route = createFileRoute("/pricing")({
  head: () =>
    buildSeo({
      title: TITLE,
      description: DESC,
      path: "/pricing",
      ogEyebrow: "Pricing",
      keywords:
        "SEO pricing Bangladesh, SEO package price, SEO cost per month, SEO retainer pricing, technical SEO audit cost, ecommerce SEO pricing, freelance SEO rates Bangladesh, AEO pricing, answer engine optimization cost, GEO pricing, generative engine optimization cost",
      alternates: { bn: "/bn/pricing" },
    }),
  component: PricingPage,
});

function PricingPage() {
  const offerSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE.url}/pricing#service`,
    name: "SEO, AEO & GEO Consulting",
    serviceType: "Search Engine Optimization",
    areaServed: ["Bangladesh", "Worldwide"],
    provider: {
      "@type": "Person",
      "@id": `${SITE.url}/#person`,
      name: SITE.fullName,
      url: `${SITE.url}/about`,
      jobTitle: "SEO Expert & Digital Marketing Consultant",
      sameAs: Object.values(SITE.social),
    },
    offers: PRICING_TIERS.map((t) => ({
      "@type": "Offer",
      "@id": `${SITE.url}/pricing#${t.slug}`,
      name: t.name,
      description: t.summary,
      category: t.cadence,
      priceCurrency: "BDT",
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "BDT",
        description: `${t.bdt} ${t.cadence} (Bangladesh) · ${t.usd} ${t.cadence} (international)`,
      },
      availability: "https://schema.org/InStock",
      url: `${SITE.url}/pricing#${t.slug}`,
    })),
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: "Pricing", path: "/pricing" },
        ]}
      />
      <h1 className="sr-only">SEO pricing and packages</h1>
      <JsonLd data={offerSchema} />

      <Section
        eyebrow="Pricing"
        title={
          <>
            What this work <span className="text-gradient">actually costs.</span>
          </>
        }
      >
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          Real numbers, published before you ever ask for a quote. Bangladeshi
          businesses are priced in taka, international clients in dollars —
          different markets, different competition, different scope.
        </p>
        <ReviewedBy reviewedOn="2026-08-21" />
      </Section>

      <PricingTierGrid tiers={PRICING_TIERS} variant="en" />

      <Section
        eyebrow="Honesty first"
        title={
          <>
            When I'll tell you <span className="text-gradient">not to hire me.</span>
          </>
        }
      >
        <PricingNotFor
          items={NOT_FOR}
          variant="en"
          bookLabel="Book a 20-minute scoping call"
          contactLabel="Prefer email? Send a brief"
        />
      </Section>

      <NextSteps />

      <Section eyebrow="FAQ" title="Pricing questions, answered plainly.">
        <FaqAccordion items={PRICING_FAQS} idPrefix="faq-pricing" />
      </Section>

      <RelatedReading
        eyebrow="Pricing & ROI"
        title={
          <>
            Read the full <span className="text-gradient">pricing research.</span>
          </>
        }
        description="Benchmarks, package breakdowns, and ROI maths for the Bangladeshi market — published in full so you can sanity-check any quote, mine included."
        hrefs={[
          "/blog/seo-service-price-in-bangladesh-2026",
          "/blog/monthly-seo-package-bangladesh-2026",
          "/blog/local-seo-cost-bangladesh-2026",
          "/blog/how-long-does-seo-take-bangladesh-2026",
          "/blog/90-day-seo-roadmap-bangladesh-small-business-2026",
        ]}
      />

      <Section className="!pt-0">
        <AiAnswersCallout
          context="SEO pricing"
          questionSlugs={["seo-cost-bangladesh", "aeo-time-to-results", "aeo-worth-it-small-business"]}
        />
      </Section>
    </>
  );
}
